/**
 * Right-click Context Menu for Quick Actions
 * Epic 007 - Story E007-S005
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBoards } from '../lib/boardsApi';
import type { BoardListItem } from '../types/boards';
import type { Campaign } from '../types/campaign';

interface ContextMenuProps {
  x: number;
  y: number;
  campaign: Campaign;
  onClose: () => void;
  onAction: (action: string, campaign: Campaign, data?: any) => void;
}

export default function ContextMenu({ x, y, campaign, onClose, onAction }: ContextMenuProps) {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x, y });
  const [showBoardsSubmenu, setShowBoardsSubmenu] = useState(false);
  const [boards, setBoards] = useState<BoardListItem[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);

  // Calculate position to keep menu in viewport
  useEffect(() => {
    if (!menuRef.current) return;

    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = x;
    let adjustedY = y;

    // Adjust horizontal position
    if (x + menuRect.width > viewportWidth) {
      adjustedX = viewportWidth - menuRect.width - 10;
    }

    // Adjust vertical position
    if (y + menuRect.height > viewportHeight) {
      adjustedY = viewportHeight - menuRect.height - 10;
    }

    setPosition({ x: Math.max(10, adjustedX), y: Math.max(10, adjustedY) });
  }, [x, y]);

  // Load boards when hovering "Add to Board"
  const handleBoardsHover = async () => {
    if (boards.length === 0 && !loadingBoards) {
      setLoadingBoards(true);
      try {
        const response = await getBoards();
        setBoards(response.boards);
      } catch (err) {
        console.error('Failed to load boards:', err);
      } finally {
        setLoadingBoards(false);
      }
    }
    setShowBoardsSubmenu(true);
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Prevent default context menu on this menu
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const menuItems = [
    {
      label: 'View Details',
      icon: '👁️',
      action: () => {
        navigate(`/dashboard?campaign=${campaign.unified_id}`);
        onClose();
      },
    },
    {
      label: campaign.status === 'active' ? 'Pause Campaign' : 'Activate Campaign',
      icon: campaign.status === 'active' ? '⏸️' : '▶️',
      action: () => {
        onAction(campaign.status === 'active' ? 'pause' : 'activate', campaign);
        onClose();
      },
    },
    {
      label: 'Add to Board',
      icon: '📌',
      hasSubmenu: true,
      onHover: handleBoardsHover,
      onLeave: () => setShowBoardsSubmenu(false),
    },
    {
      label: 'Export Data',
      icon: '📊',
      action: () => {
        onAction('export', campaign);
        onClose();
      },
    },
    {
      separator: true,
    },
    {
      label: 'Duplicate',
      icon: '📋',
      action: () => {
        onAction('duplicate', campaign);
        onClose();
      },
    },
  ];

  return (
    <>
      <div
        ref={menuRef}
        className="fixed z-50 bg-white rounded-lg shadow-2xl border border-gray-200 py-1 min-w-[200px]"
        style={{ left: position.x, top: position.y }}
        onContextMenu={handleContextMenu}
      >
        {menuItems.map((item, index) => {
          if (item.separator) {
            return <div key={index} className="h-px bg-gray-200 my-1" />;
          }

          return (
            <div
              key={index}
              className="relative"
              onMouseEnter={item.onHover}
              onMouseLeave={item.onLeave}
            >
              <button
                onClick={item.action}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center justify-between gap-2 text-sm transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="text-base">{item.icon}</span>
                  <span className="text-gray-700">{item.label}</span>
                </span>
                {item.hasSubmenu && (
                  <span className="text-gray-400">▶</span>
                )}
              </button>

              {/* Boards Submenu */}
              {item.hasSubmenu && showBoardsSubmenu && (
                <div
                  className="absolute left-full top-0 ml-1 bg-white rounded-lg shadow-2xl border border-gray-200 py-1 min-w-[180px]"
                  onMouseLeave={() => setShowBoardsSubmenu(false)}
                >
                  {loadingBoards ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      Loading boards...
                    </div>
                  ) : boards.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No boards yet
                    </div>
                  ) : (
                    boards.map((board) => (
                      <button
                        key={board.id}
                        onClick={() => {
                          onAction('add-to-board', campaign, { boardId: board.id, boardName: board.name });
                          onClose();
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-gray-700 transition-colors"
                      >
                        {board.name}
                        <span className="text-xs text-gray-500 ml-2">
                          ({board.card_count} cards)
                        </span>
                      </button>
                    ))
                  )}
                  <div className="h-px bg-gray-200 my-1" />
                  <button
                    onClick={() => {
                      navigate('/boards');
                      onClose();
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-blue-600 transition-colors"
                  >
                    + Create New Board
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
    </>
  );
}
